import * as React from 'react';
import { isSSR, setTokens } from '../../../utils/network/utils';
import { CognitoErrorCodesEnum } from '../../../utils/types/eunms';
import { useCognitoContext } from '../../providers/cognito.provider';
import type { LoginFormValues } from '@components/organisms/login-form/useLoginForm';
import type { QueryParamsErrorCodesEnum, ResponseResult } from '@utils';
import { LOGOUT_ERROR_KEY } from '@utils';

export enum LoginStepEnum {
	LOGIN = 'login',
	RESET_PASSWORD = 'reset-password',
	CHOOSE_EMAIL = 'choose-email',
	CHANGE_TEMP_PASSWORD = 'change-temp-password',
	CONFIRM = 'confirm',
}

type Props = {
	onSuccess: (data: any) => Promise<void> | void;
};

export const useLogin = ({ onSuccess }: Props) => {
	const { credentialsLogin, changePassword, confirmSignup, resendConfirmationCode } = useCognitoContext();

	const [step, setStep] = React.useState<LoginStepEnum>(LoginStepEnum.LOGIN);
	const [loginData, setLoginData] = React.useState<LoginFormValues | null>(null);

	const handleLogin = React.useCallback(
		async (data: LoginFormValues): Promise<ResponseResult<any>> => {
			const response = await credentialsLogin(data.email, data.password);

			if (response.error?.code === CognitoErrorCodesEnum.PASSWORD_RESET_REQUIRED) {
				setLoginData(data);
				setStep(LoginStepEnum.CHANGE_TEMP_PASSWORD);
			}
			// else if (response.error?.code === CognitoErrorCodesEnum.USER_NOT_CONFIRMED) {
			// 	setStep('confirm');
			// 	setLoginData(data);
			// }

			if (!response.error) {
				setTokens(response.result.credentials);
				await onSuccess(response.result);
			}

			return response;
		},
		[credentialsLogin, onSuccess]
	);

	const handleChangePassword = React.useCallback(
		async (values: { password: string }) => {
			const response = await changePassword(loginData!.email, values.password);

			if (!response.error) {
				setTokens(response.result.credentials);

				await onSuccess(response.result);
			}

			return response;
		},
		[loginData, changePassword, onSuccess]
	);

	const confirmCode = React.useCallback(
		async ({ code }: { code: string }) => {
			const response = await confirmSignup(loginData!.email, loginData!.password, code);

			if (!response.error) {
				onSuccess(response.result);
			}

			return response;
		},
		[loginData, confirmSignup]
	);

	const handleResendCode = React.useCallback((): void => {
		resendConfirmationCode(loginData!.email).catch(error => console.error(error));
	}, [resendConfirmationCode, loginData]);

	const changeStep = (step: LoginStepEnum) => setStep(step);

	const errorCode: QueryParamsErrorCodesEnum | undefined = React.useMemo(() => {
		if (isSSR()) {
			return;
		}

		const errorCode: string | null = sessionStorage?.getItem(LOGOUT_ERROR_KEY);

		sessionStorage?.removeItem(LOGOUT_ERROR_KEY);

		return errorCode as QueryParamsErrorCodesEnum;
	}, []);

	return {
		step,
		handleLogin,
		handleChangePassword,
		changeStep,
		confirmCode,
		handleResendCode,
		errorCode,
	};
};
